import { useState, useRef, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { ArrowUp, Plus, ChevronDown, ChevronRight, FolderArchive, Brain, BrainCog } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { askQuestionStream, uploadFiles, uploadBatch } from '../api';

const TYPING_SPEED = 17; // ms per character — smooth typewriter feel
const THINKING_MODELS = ['qwen3.6-plus', 'qwen3.6-max-preview'];

const MODEL_OPTIONS = [
  { key: 'auto', label: 'auto', desc: 'qwen3.5-flash' },
  { key: 'qwen3.6-plus', label: 'qwen3.6-plus' },
  { key: 'qwen3.6-max-preview', label: 'qwen3.6-max-preview', warning: 'qwen3.6-max 适配尚不完整，可能出现兼容性问题' },
];

export default function Chat({ messages, onSendMessage, onUpdateLastMessage, hasStarted, selectedFile, onUpload, onNewConversation }) {
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [displayText, setDisplayText] = useState('');
  const [thinkingText, setThinkingText] = useState('');
  const [thinkingOpen, setThinkingOpen] = useState(true);
  const [displaySources, setDisplaySources] = useState([]);
  const [statusText, setStatusText] = useState('');
  const [selectedModel, setSelectedModel] = useState('auto');
  const [thinkingEnabled, setThinkingEnabled] = useState(false);
  const [modelOpen, setModelOpen] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [mdFile, setMdFile] = useState(null);
  const [origFile, setOrigFile] = useState(null);
  const [zipFile, setZipFile] = useState(null);
  const [uploadingLocal, setUploadingLocal] = useState(false);

  const messagesEndRef = useRef(null);
  const modelDropdownRef = useRef(null);
  const uploadPanelRef = useRef(null);
  const mdInputRef = useRef(null);
  const origInputRef = useRef(null);
  const zipInputRef = useRef(null);

  // Typewriter refs
  const bufferRef = useRef('');
  const shownLenRef = useRef(0);
  const typeIntervalRef = useRef(null);
  const doneRef = useRef(false);

  // Phase tracking refs
  const thinkingTextRef = useRef('');
  const displayTextRef = useRef('');
  const hasThinkingRef = useRef(false);
  const hasAnswerRef = useRef(false);

  // Auto-set thinkingEnabled when model changes to a thinking-capable one
  useEffect(() => {
    setThinkingEnabled(THINKING_MODELS.includes(selectedModel));
  }, [selectedModel]);

  // Auto-scroll
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, displayText, thinkingText, statusText]);

  // Close dropdowns on click outside
  useEffect(() => {
    function onDocClick(e) {
      if (modelDropdownRef.current && !modelDropdownRef.current.contains(e.target)) {
        setModelOpen(false);
      }
      if (uploadPanelRef.current && !uploadPanelRef.current.contains(e.target)) {
        const plusBtn = document.getElementById('chat-plus-btn');
        if (!plusBtn || !plusBtn.contains(e.target)) {
          setUploadOpen(false);
        }
      }
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  // ── Typewriter engine ──
  const stopTyping = useCallback(() => {
    if (typeIntervalRef.current) {
      clearInterval(typeIntervalRef.current);
      typeIntervalRef.current = null;
    }
  }, []);

  const startTyping = useCallback(() => {
    stopTyping();
    typeIntervalRef.current = setInterval(() => {
      const buf = bufferRef.current;
      const shown = shownLenRef.current;
      if (shown < buf.length) {
        shownLenRef.current = shown + 1;
        setDisplayText(buf.slice(0, shown + 1));
        displayTextRef.current = buf.slice(0, shown + 1);
      } else if (doneRef.current) {
        // Done typing — mark loading complete
        clearInterval(typeIntervalRef.current);
        typeIntervalRef.current = null;
        setLoading(false);
      }
    }, TYPING_SPEED);
  }, [stopTyping]);

  const flushRemaining = useCallback(() => {
    // Quickly flush remaining buffer
    stopTyping();
    const buf = bufferRef.current;
    shownLenRef.current = buf.length;
    setDisplayText(buf);
    displayTextRef.current = buf;
    setLoading(false);
  }, [stopTyping]);

  // ── Submit ──

  const handleSubmit = useCallback(async () => {
    if (!input.trim() || loading) return;
    const question = input.trim();
    setInput('');

    onSendMessage({ role: 'user', content: question });
    setLoading(true);
    onSendMessage({ role: 'assistant', content: '', sources: [] });

    // Reset all streaming state
    bufferRef.current = '';
    shownLenRef.current = 0;
    doneRef.current = false;
    setDisplayText('');
    setThinkingText('');
    setThinkingOpen(true);
    setDisplaySources([]);
    setStatusText('正在连接...');
    thinkingTextRef.current = '';
    displayTextRef.current = '';
    hasThinkingRef.current = false;
    hasAnswerRef.current = false;
    stopTyping();

    // Build history for context (last 10 messages with content)
    const history = messages
      .filter(m => m.content && (m.role === 'user' || m.role === 'assistant'))
      .map(m => ({ role: m.role, content: m.content }))
      .slice(-10);

    await askQuestionStream(question, {
      onStatus: (status) => {
        setStatusText(status);
      },
      onThinking: (token) => {
        if (!hasThinkingRef.current) {
          hasThinkingRef.current = true;
        }
        thinkingTextRef.current += token;
        setThinkingText(thinkingTextRef.current);
      },
      onThinkingDone: () => {
        // thinking complete
      },
      onToken: (token) => {
        if (!hasAnswerRef.current) {
          hasAnswerRef.current = true;
          setThinkingOpen(false); // collapse thinking panel when answer starts
        }
        bufferRef.current += token;
        if (!typeIntervalRef.current) {
          startTyping();
        }
      },
      onDone: (sources) => {
        doneRef.current = true;
        setDisplaySources(sources);
        // If typewriter already finished, flush and mark done
        if (shownLenRef.current >= bufferRef.current.length) {
          flushRemaining();
        }
        // Save final content to message (typewriter still running is fine —
        // the interval will setLoading(false) when done)
        onUpdateLastMessage({
          content: bufferRef.current,
          sources,
          thinking: thinkingTextRef.current,
        });
        // Safety: ensure loading stops within 5s
        setTimeout(() => {
          if (typeIntervalRef.current) {
            flushRemaining();
          }
        }, 5000);
      },
      onError: (err) => {
        doneRef.current = true;
        stopTyping();
        setStatusText('');
        setLoading(false);
        const errText = `抱歉，请求出错：${err}`;
        bufferRef.current = errText;
        shownLenRef.current = errText.length;
        setDisplayText(errText);
        displayTextRef.current = errText;
        onUpdateLastMessage({ content: errText });
      },
    }, {
      model: selectedModel,
      history,
      thinking: thinkingEnabled,
    });
  }, [input, loading, messages, onSendMessage, onUpdateLastMessage, selectedModel, thinkingEnabled, startTyping, stopTyping, flushRemaining]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  // ── Upload ──

  const handleUpload = async () => {
    if (!origFile) return;
    setUploadingLocal(true);
    try {
      await uploadFiles(mdFile, origFile);
      setMdFile(null);
      setOrigFile(null);
      if (mdInputRef.current) mdInputRef.current.value = '';
      if (origInputRef.current) origInputRef.current.value = '';
      setUploadOpen(false);
      if (onUpload) onUpload();
    } catch (e) {
      alert('上传失败: ' + (e.response?.data?.detail || e.message));
    } finally {
      setUploadingLocal(false);
    }
  };

  const handleBatchUpload = async () => {
    if (!zipFile) return;
    setUploadingLocal(true);
    try {
      await uploadBatch(zipFile);
      setZipFile(null);
      if (zipInputRef.current) zipInputRef.current.value = '';
      setUploadOpen(false);
      if (onUpload) onUpload();
    } catch (e) {
      alert('批量上传失败: ' + (e.response?.data?.detail || e.message));
    } finally {
      setUploadingLocal(false);
    }
  };

  const currentModel = MODEL_OPTIONS.find(m => m.key === selectedModel);

  return (
    <div className="flex flex-col h-full w-full bg-gray-50">
      {/* Top bar */}
      {hasStarted && (
        <div className="h-10 flex items-center justify-between px-4 border-b border-gray-200 bg-white/80 backdrop-blur-sm flex-shrink-0">
          <span className="text-sm font-medium text-gray-700">AI 问答</span>
          {selectedFile && (
            <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full truncate max-w-xs">
              {selectedFile.base_name}
            </span>
          )}
        </div>
      )}

      {/* Messages area */}
      {hasStarted && (
        <div className="flex-1 overflow-y-auto px-4 py-6">
          <div className="max-w-3xl mx-auto space-y-4">
            {messages.length === 0 && (
              <div className="text-center text-gray-400 text-sm py-8">开始提问吧</div>
            )}
            {messages.map((msg, idx) => {
              const isLastAssistant = msg.role === 'assistant' && idx === messages.length - 1;
              const isStreaming = isLastAssistant && loading;
              const showText = isStreaming ? displayText : (msg.content || '');
              const showThinking = isStreaming ? thinkingText : (msg.thinking || '');
              const showSources = isStreaming ? displaySources : msg.sources;
              const thinkingActive = isStreaming && hasThinkingRef.current && typeIntervalRef.current;
              const answerStarted = isStreaming && hasAnswerRef.current;
              const showStatus = isStreaming && statusText && !hasThinkingRef.current && !hasAnswerRef.current;
              const showCursor = isStreaming && answerStarted && typeIntervalRef.current;

              return (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 12, scale: 0.98 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  transition={{ duration: 0.35, ease: 'easeOut' }}
                  className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  {msg.role === 'user' ? (
                    <div className="bg-gray-100 rounded-2xl px-4 py-2.5 text-sm text-gray-900 max-w-xl whitespace-pre-wrap">
                      {msg.content}
                    </div>
                  ) : (
                    <div className="bg-white border border-gray-200 shadow-sm rounded-2xl rounded-tl-sm px-4 py-3 text-sm text-gray-800 max-w-xl w-full">
                      {/* Thinking chain — streamed in real-time */}
                      {showThinking && (
                        <div className="mb-3 border border-amber-100 rounded-lg overflow-hidden">
                          <button
                            onClick={() => isStreaming ? setThinkingOpen(v => !v) : null}
                            className="w-full flex items-center space-x-1.5 px-3 py-1.5 text-xs text-amber-700 bg-amber-50 hover:bg-amber-100 transition-colors cursor-pointer"
                          >
                            <Brain size={13} className="text-amber-500" />
                            <span>{thinkingActive ? '正在思考...' : '思考过程'}</span>
                            {isStreaming ? (
                              thinkingOpen
                                ? <ChevronDown size={13} className="ml-auto" />
                                : <ChevronRight size={13} className="ml-auto" />
                            ) : (
                              <ChevronDown size={13} className="ml-auto" />
                            )}
                          </button>
                          {thinkingOpen && (
                            <div className="px-3 py-2 bg-amber-50/50 text-xs text-amber-800 leading-relaxed whitespace-pre-wrap max-h-48 overflow-y-auto border-t border-amber-100">
                              {showThinking}
                            </div>
                          )}
                        </div>
                      )}

                      {/* Status text — shows real backend status */}
                      {showStatus && (
                        <div className="flex items-center space-x-2">
                          <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-pulse" />
                          <span className="text-gray-400 text-sm">{statusText}</span>
                        </div>
                      )}

                      {/* Answer content with typewriter */}
                      <div className="prose prose-sm max-w-none">
                        {showText ? (
                          <ReactMarkdown>{showText}</ReactMarkdown>
                        ) : (!showStatus && !showThinking) ? (
                          <span className="text-gray-400 text-sm">{statusText}</span>
                        ) : null}
                      </div>

                      {/* Blinking cursor during typing */}
                      {showCursor && (
                        <span className="inline-block w-1.5 h-4 bg-gray-400 ml-0.5 align-middle animate-pulse" />
                      )}

                      {/* Sources — only shown when streaming is fully done */}
                      {showSources && showSources.length > 0 && !loading && (
                        <p className="text-xs text-gray-400 mt-2 pt-2 border-t border-gray-100">
                          来源：{showSources.join(' · ')}
                        </p>
                      )}
                    </div>
                  )}
                </motion.div>
              );
            })}
            <div ref={messagesEndRef} />
          </div>
        </div>
      )}

      {/* Input area */}
      <motion.div
        layout
        transition={{ type: 'spring', stiffness: 380, damping: 32 }}
        className={`w-full px-4 ${
          hasStarted
            ? 'py-3 border-t border-gray-200 bg-white flex-shrink-0'
            : 'flex-1 flex items-center justify-center'
        }`}
      >
        <div className={`w-full ${hasStarted ? 'max-w-3xl mx-auto' : 'max-w-2xl'}`}>
          {!hasStarted && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1, duration: 0.4 }}
              className="mb-8 text-center"
            >
              <h1 className="text-2xl font-semibold text-gray-900 tracking-tight">Argon 知识库</h1>
              <p className="text-sm text-gray-400 mt-2">上传文档后，向我提问任何相关内容</p>
            </motion.div>
          )}

          <motion.div
            layout
            transition={{ type: 'spring', stiffness: 380, damping: 32 }}
            className={`relative bg-white border border-gray-200 transition-shadow duration-300 ${
              hasStarted ? 'shadow-sm rounded-xl' : 'shadow-lg rounded-2xl'
            }`}
          >
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={hasStarted ? '继续对话...' : '向知识库提问...'}
              rows={hasStarted ? 1 : 3}
              className="w-full bg-transparent px-4 py-3 text-sm text-gray-900 placeholder-gray-400 outline-none resize-none rounded-2xl"
            />
            <div className="flex items-center justify-between px-3 pb-2.5">
              <div className="flex items-center space-x-1">
                {/* Upload button */}
                <div className="relative">
                  <button
                    id="chat-plus-btn"
                    onClick={() => setUploadOpen((v) => !v)}
                    className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors text-gray-500"
                  >
                    <Plus size={16} />
                  </button>

                  {uploadOpen && (
                    <div
                      ref={uploadPanelRef}
                      className="absolute bottom-full left-0 mb-2 w-72 bg-white border border-gray-200 rounded-xl shadow-lg p-3 z-50"
                    >
                      <input type="file" accept=".md" ref={mdInputRef} onChange={(e) => setMdFile(e.target.files?.[0] || null)} className="hidden" />
                      <input type="file" accept=".pdf,.mp4,.mov,.avi,.mkv" ref={origInputRef} onChange={(e) => setOrigFile(e.target.files?.[0] || null)} className="hidden" />
                      <input type="file" accept=".zip" ref={zipInputRef} onChange={(e) => setZipFile(e.target.files?.[0] || null)} className="hidden" />

                      <div className="space-y-1.5">
                        <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">单文件上传</p>
                        <button
                          onClick={() => origInputRef.current?.click()}
                          className={`w-full text-left text-xs px-2.5 py-1.5 rounded-md border border-dashed transition-colors ${
                            origFile ? 'border-green-300 bg-green-50 text-green-700' : 'border-gray-300 text-gray-500 hover:border-gray-400 hover:bg-gray-50'
                          }`}
                        >
                          {origFile ? `✓ ${origFile.name}` : '选择 PDF / 视频 *'}
                        </button>
                        <button
                          onClick={() => mdInputRef.current?.click()}
                          className={`w-full text-left text-xs px-2.5 py-1.5 rounded-md border border-dashed transition-colors ${
                            mdFile ? 'border-green-300 bg-green-50 text-green-700' : 'border-gray-300 text-gray-500 hover:border-gray-400 hover:bg-gray-50'
                          }`}
                        >
                          {mdFile ? `✓ ${mdFile.name}` : '选择 .md 文件（可选）'}
                        </button>
                        <button
                          onClick={handleUpload}
                          disabled={!origFile || uploadingLocal}
                          className={`w-full text-xs py-1.5 rounded-md font-medium transition-colors ${
                            origFile && !uploadingLocal ? 'bg-gray-900 text-white hover:bg-gray-800' : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                          }`}
                        >
                          {uploadingLocal ? '上传中...' : '上传'}
                        </button>
                        {origFile && origFile.name.endsWith('.pdf') && !mdFile && (
                          <p className="text-[10px] text-amber-600">未选择 .md，将自动提取 PDF 文本</p>
                        )}
                      </div>

                      <div className="space-y-1.5 pt-2 mt-2 border-t border-gray-100">
                        <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">批量上传</p>
                        <button
                          onClick={() => zipInputRef.current?.click()}
                          className={`w-full flex items-center space-x-1.5 text-left text-xs px-2.5 py-1.5 rounded-md border border-dashed transition-colors ${
                            zipFile ? 'border-green-300 bg-green-50 text-green-700' : 'border-gray-300 text-gray-500 hover:border-gray-400 hover:bg-gray-50'
                          }`}
                        >
                          <FolderArchive size={14} />
                          <span>{zipFile ? `✓ ${zipFile.name}` : '选择文件夹 Zip'}</span>
                        </button>
                        <button
                          onClick={handleBatchUpload}
                          disabled={!zipFile || uploadingLocal}
                          className={`w-full text-xs py-1.5 rounded-md font-medium transition-colors ${
                            zipFile && !uploadingLocal ? 'bg-gray-900 text-white hover:bg-gray-800' : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                          }`}
                        >
                          {uploadingLocal ? '上传中...' : '批量上传'}
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Thinking toggle */}
                <button
                  onClick={() => setThinkingEnabled(v => !v)}
                  className={`p-1.5 rounded-lg transition-all duration-200 ${
                    thinkingEnabled
                      ? 'text-amber-600 bg-amber-50 hover:bg-amber-100'
                      : 'text-gray-400 hover:bg-gray-100 hover:text-gray-500'
                  }`}
                  title={thinkingEnabled ? '深度思考已开启' : '深度思考已关闭'}
                >
                  <BrainCog size={16} />
                </button>

                {/* Model selector */}
                <div className="relative" ref={modelDropdownRef}>
                  <button
                    onClick={() => setModelOpen((v) => !v)}
                    className="flex items-center space-x-0.5 text-xs text-gray-500 hover:bg-gray-100 px-2 py-1 rounded-lg transition-colors"
                  >
                    <span>{selectedModel === 'auto' ? 'auto' : selectedModel}</span>
                    {currentModel?.desc && (
                      <span className="text-[10px] text-gray-400">({currentModel.desc})</span>
                    )}
                    <ChevronDown size={12} />
                  </button>
                  {modelOpen && (
                    <div className="absolute bottom-full left-0 mb-1 w-52 bg-white border border-gray-200 rounded-lg shadow-lg py-1 z-50">
                      {MODEL_OPTIONS.map((m) => (
                        <div key={m.key} className="relative group/model">
                          <button
                            onClick={() => {
                              setSelectedModel(m.key);
                              setModelOpen(false);
                            }}
                            className={`w-full text-left px-3 py-1.5 text-xs transition-colors flex items-center space-x-1.5 ${
                              selectedModel === m.key ? 'bg-gray-100 text-gray-900 font-medium' : 'text-gray-600 hover:bg-gray-50'
                            }`}
                          >
                            <span>{m.label}</span>
                            {m.desc && <span className="text-[10px] text-gray-400">({m.desc})</span>}
                          </button>
                          {m.warning && (
                            <div className="absolute left-full ml-2 top-0 w-48 bg-amber-50 border border-amber-200 rounded-md px-2.5 py-1.5 text-[10px] text-amber-800 leading-relaxed opacity-0 invisible group-hover/model:opacity-100 group-hover/model:visible transition-all duration-150 z-50 shadow-sm">
                              {m.warning}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <button
                onClick={handleSubmit}
                disabled={!input.trim() || loading}
                className={`p-2 rounded-full transition-all duration-200 ${
                  input.trim() && !loading ? 'bg-gray-900 text-white hover:bg-gray-700 shadow-sm' : 'bg-gray-200 text-gray-400'
                }`}
              >
                <ArrowUp size={16} />
              </button>
            </div>
          </motion.div>

          {!hasStarted && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3, duration: 0.4 }}
              className="flex items-center justify-center space-x-2 mt-5"
            >
              <span className="text-xs text-gray-500 px-3 py-1.5 bg-white border border-gray-200 rounded-full cursor-pointer hover:border-gray-300 hover:bg-gray-50 transition-colors">
                基于当前文档提问
              </span>
              <span className="text-xs text-gray-500 px-3 py-1.5 bg-white border border-gray-200 rounded-full cursor-pointer hover:border-gray-300 hover:bg-gray-50 transition-colors">
                总结文档要点
              </span>
            </motion.div>
          )}
        </div>
      </motion.div>
    </div>
  );
}
